import { loadRecordSnapshot } from "../../../collaboration/loadRecordSnapshot";
import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { Grid } from "@mui/material";
import { FormikSaveBar } from "../../utils/FormikSaveBar";
import { PMField } from "../../utils/PMField";
import { useDispatch, useSelector } from "react-redux";
import PMSwitch from "../../utils/PMSwitch";
import { createOrderAsync, updateOrderAsync } from "../../../slices/order";
import { RecordCollaborationPanel } from "../../../collaboration/RecordCollaborationPanel";
import { normalizeError } from "../../../operationFeedback";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { PMButton } from "../../utils/PMButton";
import PMCombobox from "../../utils/PMCombobox";
import { orderStatusOptions } from "./utils";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { ContactsSelector } from "../../utils/moduleSelectors/contactSelector";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { DepartmentSelector } from "../../utils/moduleSelectors/departmentSelector";
import { DeliveryAddressSelector } from "../../utils/moduleSelectors/deliveryAddressSelector";
import OrderItemsV3 from "./order-items-v3";

export const orderFormValues = (order, currentUser, currentDate) => ({
            id: order?.id,
            edit_version: order?.edit_version,
            customer_id: order?.customer?.id,
            project_id: order?.project?.id,
            department_id: order?.department?.id,
            order_number: order?.order_number,
            active: order?.active,
            reference: order?.reference,
            contact_id: order?.contact?.id,
            our_contact_employee_id:
              order?.our_contact_employee?.id || currentUser?.id,
            order_date: order?.order_date || currentDate,
            delivery_date: order?.delivery_date || currentDate,
            delivery_address: order?.delivery_address,
            status: order?.status,
            items: order?.items,
            groups: order?.order_groups,
            comment: order?.comment,
          });

export const OrderInfo = (props) => {
  const { t } = useTranslation();
  const { order, isLoading, showWarehousePick } = props;
  const dispatch = useDispatch();

  const updateOrCreateOrder = (values) => {
    if (values.id) {
      return dispatch(updateOrderAsync(values));
    } else {
      return dispatch(createOrderAsync(values));
    }
  };

  const currentDate = new Date().toISOString().slice(0, 10);

  const [optionalOrderNumber, setOptionalOrderNumber] = useState(false);

  const [selectedDepartment, setSelectedDepartment] = useState(
    order?.department?.id ? order?.department : null
  );

  const handleProjectChange = (data, formik) => {
    const { setFieldValue, values } = formik;

    if (data?.department?.id && !values.department_id) {
      setFieldValue("department_id", data?.department?.id);
      setSelectedDepartment(data?.department);
    }
  };

  const currentUser = useSelector((state) => state.session.currentUser);

  return (
    <>
      {isLoading ? (
        <ResourceLoading />
      ) : (
        <Formik
          initialValues={orderFormValues(order, currentUser, currentDate)}
          validationSchema={Yup.object({
            title: Yup.string().max(255),
            customer_id: Yup.string().required("Customer is required"),
          })}
          onSubmit={async (values, helpers) => {
            try {
              helpers.setStatus(undefined);
              const result = await updateOrCreateOrder(values);
              if (result.meta?.requestStatus === "rejected") throw result.payload || result.error;
              const saved = result.payload;
              // Confirmed save only — do not treat a later UI refresh failure as save failure.
              helpers.resetForm({ values: { ...values, ...saved, groups: saved.order_groups || [], edit_version: saved.edit_version } });
              toast.success(t("order.save.success"));
            } catch (error) {
              const problem = normalizeError(error, { operationKey: "update_order" });
              helpers.setStatus({ problem });
              // OF propose-on-touch: durable field/status problem + one toast (no false success).
              // Follow-up: migrate this save to ActionDialog for multi-step (items + Tripletex sync).
              if (problem?.safeMessage) {
                toast.error(problem.safeMessage);
              }
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => (
            <Form>
              <RecordCollaborationPanel record={formik.values} recordType="order" section="info" editing={formik.dirty} aboveSaveBar={formik.dirty}
                saving={formik.isSubmitting}
                onRefresh={async () => {
                  const fresh = await loadRecordSnapshot("order", order.id);
                  return { apply: () => {
                    formik.resetForm({ values: orderFormValues(fresh, currentUser, currentDate) });
                    setSelectedDepartment(fresh.department || null);
                    props.onSnapshot?.(fresh);
                  } };
                }}
                needsReview={Boolean(formik.status?.problem)} errorMessage={formik.status?.problem?.safeMessage} />
              <fieldset disabled={formik.isSubmitting} className="min-w-0">
              <Grid item xs={12}>
                <div className="shadow sm:overflow-auto sm:rounded-md">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div>
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {t("order.info")}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500"></p>
                    </div>

                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 sm:col-span-6">
                        {!formik.values.order_number ? (
                          <PMField
                            name="optionalOrderNumber"
                            type="checkbox"
                            label={t("Optional Order Number")}
                            onChange={() =>
                              setOptionalOrderNumber(!optionalOrderNumber)
                            }
                            {...formik}
                          />
                        ) : null}
                      </div>

                      <div className="col-span-6 sm:col-span-2">
                        <PMField
                          disabled={optionalOrderNumber}
                          name="order_number"
                          type="text"
                          label={t("order_number")}
                          {...formik}
                        />
                      </div>

                      <div className="col-span-6 sm:col-span-2">
                        <PMField
                          name="order_date"
                          type="date"
                          label={t("order_date")}
                          {...formik}
                        />
                      </div>

                      <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                        <PMField
                          name="reference"
                          type="text"
                          label={t("reference")}
                          {...formik}
                        />
                      </div>

                      <div className="col-span-6 sm:col-span-2">
                        <CustomerSelector
                          selected={
                            order?.customer?.id ? order?.customer : null
                          }
                          {...formik}
                        />
                      </div>

                      <div className="col-span-6 sm:col-span-2">
                        <ContactsSelector
                          selected={order?.contact?.id ? order?.contact : null}
                          module_param={order?.customer?.id}
                          label="Customer Contact"
                          name="contact_id"
                          {...formik}
                        />
                      </div>

                      <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                        <ProjectSelector
                          selected={order?.project?.id ? order?.project : null}
                          onChange={(data) => handleProjectChange(data, formik)}
                          {...formik}
                        />
                      </div>

                      <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                        <EmployeeSelector
                          name="our_contact_employee_id"
                          label="Our Employee Contact"
                          selected={
                            order?.our_contact_employee?.id
                              ? order?.our_contact_employee
                              : currentUser
                          }
                          {...formik}
                        />
                      </div>

                      <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                        <DepartmentSelector
                          selected={selectedDepartment}
                          {...formik}
                        />
                      </div>

                      <div className="col-span-6">
                        <PMField
                          name="comment"
                          type="text"
                          label="Comment"
                          multiline
                          rows={2}
                          {...formik}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sx={{ mt: 3 }}>
                <div className="shadow sm:rounded-md">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    {/* <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">{ t("Product Description") }</h3>
                    <p className="mt-1 text-sm text-gray-500"></p>
                  </div> */}
                    <div className="grid sm:grid-cols-8 gap-6">
                      <div className="sm:col-span-4">
                        <PMSwitch label="Active" name="active" {...formik} />
                      </div>

                      <div className="col-span-4 sm:col-span-4">
                        <PMCombobox
                          name="status"
                          label={t("Status")}
                          options={orderStatusOptions}
                          {...formik}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sx={{ mt: 3 }}>
                <div className="shadow sm:overflow-hidden sm:rounded-md">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div>
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {t("Delivery Information")}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500"></p>
                    </div>

                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 sm:col-span-3">
                        <PMField
                          name="delivery_date"
                          type="date"
                          label={t("Delivery Date")}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <DeliveryAddressSelector
                          selected={order?.delivery_address || null}
                          name="delivery_address_id"
                          label={t("Delivery Address")}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <PMField
                          disabled
                          name="shipping_brand"
                          type="text"
                          label={t("Sipping Brand")}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <PMField
                          disabled
                          name="sipping_number"
                          type="text"
                          label={t("Sipping Number")}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <PMButton
                          className="mt-6"
                          onClick={() =>
                            toast.error("Function is not available yet!")
                          }
                        >
                          {t("Create shipping label")}
                        </PMButton>
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sx={{ mt: 3 }}>
                <OrderItemsV3
                  disabled={order?.active === false}
                  group={true}
                  sales_price={true}
                  purchase_price={true}
                  discount={true}
                  surcharge={true}
                  showWarehousePick={showWarehousePick}
                  {...formik}
                />
              </Grid>

              </fieldset>
              <FormikSaveBar {...formik} disabled={formik.isSubmitting} />
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};
